import type { GetShareSettingsResponse } from '@orbit/api-types'
import { TOGGLEABLE_SHOW_FLAG_KEYS } from './shareFlags'
import React from 'react'
import {
	CopyLinkArea,
	CopyLinkButton,
	CopyLinkHint,
	Divider,
	LoadingText,
	MasterStatus,
	SectionLabel,
	SectionList,
	ShareDescription,
	ShareRow,
	ShareText,
	ShareTitle,
	ToggleInput,
	ToggleSlider,
	ToggleWrapper,
	WarningNote,
} from './SharingSection.styles'

type SectionFlag = keyof typeof TOGGLEABLE_SHOW_FLAG_KEYS

interface SectionCopy {
	label: string
	description: string
	/** Always-visible warning for flags that expose sensitive data (ADR 0003). */
	warning?: string
}

/**
 * Copy keyed by the toggleable flag map: adding a flag to
 * TOGGLEABLE_SHOW_FLAG_KEYS fails type-check here until copy exists for it, and a
 * label can never be paired with the wrong key.
 */
const SECTION_COPY: Record<SectionFlag, SectionCopy> = {
	publicShowItinerary: {
		label: 'Itinerary',
		description: 'Shows your day-by-day plan — events, times, and places.',
	},
	publicShowIdeas: {
		label: 'Ideas',
		description: 'Shows the ideas and suggestions saved for this trip.',
	},
	publicShowAccommodations: {
		label: 'Accommodations',
		description: "Shows where you're staying, with check-in and check-out dates.",
	},
	publicShowFlights: {
		label: 'Flights',
		description: 'Shows flight numbers, times, and airports.',
	},
	publicShowExpenses: {
		label: 'Expenses & pay ups',
		description: 'Shows the group money summary on the public link.',
		warning:
			'Turning this on publishes named balances and recorded pay ups — anyone with the link can see who owes whom, and how much.',
	},
}

/** Display order comes from the toggleable key map, so UI order cannot drift from it. */
const SECTION_ORDER = Object.keys(TOGGLEABLE_SHOW_FLAG_KEYS) as SectionFlag[]

interface SharingSectionProps {
	/** The Sharing surface is an owner-only decision (ADR 0003); members see nothing. */
	isOwner: boolean
	settings: GetShareSettingsResponse | undefined
	isLoading: boolean
	/** Flags with a PATCH in flight — each locks only its own row (toggles may overlap). */
	savingKeys: ReadonlySet<keyof GetShareSettingsResponse>
	onToggle: (field: keyof GetShareSettingsResponse, value: boolean) => void
	onCopyLink: () => void
}

const SharingSection: React.FC<SharingSectionProps> = ({
	isOwner,
	settings,
	isLoading,
	savingKeys,
	onToggle,
	onCopyLink,
}) => {
	if (!isOwner) return null

	if (!settings) {
		return (
			<>
				<Divider />
				<SectionLabel>Sharing</SectionLabel>
				<LoadingText>{isLoading ? 'Loading sharing settings…' : 'Sharing settings unavailable.'}</LoadingText>
			</>
		)
	}

	const masterOn = settings.isVisibleToPublic
	const masterSaving = savingKeys.has('isVisibleToPublic')

	return (
		<>
			<Divider />
			<SectionLabel>Sharing</SectionLabel>

			<ShareRow $disabled={masterSaving}>
				<ShareText>
					<ShareTitle>Share this trip</ShareTitle>
					<ShareDescription>
						Turns on a public link anyone can open — no Orbit account needed. Each section below
						controls what that link reveals.
					</ShareDescription>
					<MasterStatus $isPublic={masterOn}>
						{masterSaving ? 'Saving…' : masterOn ? '● Link is live' : '○ Link is off'}
					</MasterStatus>
				</ShareText>
				<ToggleWrapper>
					<ToggleInput
						type="checkbox"
						id="share-master"
						aria-label="Share this trip"
						checked={masterOn}
						disabled={masterSaving}
						onChange={() => onToggle('isVisibleToPublic', !masterOn)}
					/>
					<ToggleSlider htmlFor="share-master" $checked={masterOn} $disabled={masterSaving} />
				</ToggleWrapper>
			</ShareRow>

			<SectionList>
				{SECTION_ORDER.map(key => {
					const { label, description, warning } = SECTION_COPY[key]
					const checked = settings[key]
					// Section flags are meaningless until the link is live (ADR 0003).
					const disabled = !masterOn || savingKeys.has(key)
					const inputId = `share-toggle-${key}`

					return (
						<ShareRow key={key} $disabled={disabled}>
							<ShareText>
								<ShareTitle>{label}</ShareTitle>
								<ShareDescription>{description}</ShareDescription>
								{warning && <WarningNote role="note">{warning}</WarningNote>}
							</ShareText>
							<ToggleWrapper>
								<ToggleInput
									type="checkbox"
									id={inputId}
									aria-label={label}
									checked={checked}
									disabled={disabled}
									onChange={() => onToggle(key, !checked)}
								/>
								<ToggleSlider htmlFor={inputId} $checked={checked} $disabled={disabled} />
							</ToggleWrapper>
						</ShareRow>
					)
				})}
			</SectionList>

			<CopyLinkArea>
				<CopyLinkButton
					type="button"
					onClick={onCopyLink}
					disabled={!masterOn}
					title={masterOn ? undefined : 'Turn on “Share this trip” to get a public link'}
				>
					Copy public link
				</CopyLinkButton>
				{!masterOn && (
					<CopyLinkHint>Turn on “Share this trip” to get a public link.</CopyLinkHint>
				)}
			</CopyLinkArea>
		</>
	)
}

export default SharingSection
